import React from 'react';

import { cn } from '@/lib/utils';
import { ShotText } from '../studio-contract';

/**
 * The shot's on-screen text, laid over its picture as the film will show it.
 *
 * Sized in container units so it reads the same on a small card and a large
 * preview; the film's own typesetting lives in film.ts, and this is its
 * approximation, close enough to judge the words and the placement.
 */
export const TextPreview: React.FC<{ text: ShotText; className?: string }> = ({ text, className }) => {
    const pos = text.position || (text.role === 'label' ? 'bottom-right' : text.role === 'title' ? 'center' : 'bottom');
    const family = text.font === 'serif' ? '"Cormorant Garamond", "Playfair Display", Georgia, serif' : 'inherit';
    // Titles shrink as they get longer: one word can fill the frame, a line cannot.
    const len = text.content.length;
    const title = len <= 10 ? 9 : len <= 18 ? 7 : len <= 30 ? 5.5 : 4.5;
    const size = text.role === 'title' ? `${title}cqh` : text.role === 'label' ? '3.4cqh' : '3.8cqh';
    return (
        <div className={cn('pointer-events-none absolute inset-0 flex p-[6%]', className)} style={{ containerType: 'size' } as any}>
            <div
                className={cn(
                    'flex w-full',
                    pos === 'top' && 'items-start justify-center',
                    pos === 'center' && 'items-center justify-center',
                    pos === 'bottom' && 'items-end justify-center pb-[10%]',
                    pos === 'bottom-right' && 'items-end justify-end pb-[10%]',
                    pos === 'bottom-left' && 'items-end justify-start pb-[10%]',
                )}
            >
                <span
                    className="text-center text-white"
                    style={{
                        fontFamily: family,
                        fontSize: size,
                        lineHeight: 1.05,
                        fontWeight: 400,
                        letterSpacing: text.role === 'label' ? '0.08em' : text.role === 'title' ? '-0.01em' : 0,
                        textTransform: text.role === 'label' ? 'uppercase' : 'none',
                        textShadow: '0 1px 12px rgba(0,0,0,0.35)',
                    }}
                >
                    {text.content}
                </span>
            </div>
        </div>
    );
};
